// フロントエンド用のロガー（開発環境でのみ使用）

/** `logger.error` 第2引数用に unknown を Error に正規化 */
export function toLoggableError(err: unknown): Error {
  return err instanceof Error ? err : new Error(String(err));
}

export enum LogLevel {
  ERROR = 'ERROR',
  WARN = 'WARN',
  INFO = 'INFO',
  DEBUG = 'DEBUG',
}

class Logger {
  private shouldLog(level: LogLevel): boolean {
    // DEBUG は既定オフ（ユーザー情報・ファイル一覧などがコンソールに出るため）。必要時のみ .env に VITE_DEBUG_LOGS=true
    if (level === LogLevel.DEBUG) {
      return import.meta.env.DEV && import.meta.env.VITE_DEBUG_LOGS === 'true';
    }
    return true;
  }

  private formatMessage(level: LogLevel, message: string, context?: Record<string, any>): string {
    const timestamp = new Date().toISOString();
    let logMessage = `[${timestamp}] [${level}] ${message}`;
    
    if (context && Object.keys(context).length > 0) {
      logMessage += ` | Context: ${JSON.stringify(context)}`;
    }
    
    return logMessage;
  }

  error(message: string, error?: Error, context?: Record<string, any>): void {
    if (!this.shouldLog(LogLevel.ERROR)) return;
    
    const logMessage = this.formatMessage(LogLevel.ERROR, message, context);
    console.error(logMessage, error || '');
    
    // エラーはAPIに送信することも可能（将来の拡張）
    if (error && import.meta.env.PROD) {
      // 本番環境ではエラートラッキングサービスに送信
      // 例: Sentry, LogRocket等
    }
  }

  warn(message: string, context?: Record<string, any>): void {
    if (!this.shouldLog(LogLevel.WARN)) return;
    console.warn(this.formatMessage(LogLevel.WARN, message, context));
  }

  info(message: string, context?: Record<string, any>): void {
    if (!this.shouldLog(LogLevel.INFO)) return;
    console.log(this.formatMessage(LogLevel.INFO, message, context));
  }

  debug(message: string, context?: Record<string, any>): void {
    if (!this.shouldLog(LogLevel.DEBUG)) return;
    console.debug(this.formatMessage(LogLevel.DEBUG, message, context));
  }
}

export const logger = new Logger();

